import { LegalLayout } from '../components/LegalLayout';

export function DocsPage() {
  return (
    <LegalLayout title="Documentation" lastUpdated="July 21, 2026">
      <p>
        PROTBYTE Sentinel is a free, browser-based external security and compliance
        posture tool. Use it from the{' '}
        <a href="/">home page</a>: enter a domain you own or are authorized to
        assess, complete the quick human-verification check, and you get a full
        report in a few seconds.
      </p>

      <p>
        <strong>Authorization required.</strong> Only submit domains you own or have
        explicit permission to assess. The scanner actively probes the target. See
        the <a href="/aup">Acceptable Use Policy</a> and{' '}
        <a href="/terms">Terms of Service</a>.
      </p>

      <h2>Not a public API</h2>
      <p>
        The scan endpoint is intended for interactive use through this website only.
        It is protected by a human-verification challenge (Cloudflare Turnstile),
        and automated, scripted, bulk, or programmatic access is{' '}
        <strong>not supported and is prohibited</strong> by the{' '}
        <a href="/aup">Acceptable Use Policy</a>. There is no public API key and no
        bulk-scanning endpoint.
      </p>
      <p>
        The only unauthenticated endpoint is a liveness probe:
      </p>
      <pre className="codeblock-light">{`GET https://api.protbyte.com/api/health
{"ok":true,"version":"0.5.0","ts":1714680000000}`}</pre>

      <h2>What the analyzers check</h2>
      <p>
        See the <a href="/">home page tiles</a> for the full list. In short: DNS
        records + DNSSEC + nameserver redundancy, SPF (with RFC 7208 lookup-limit
        validation) / DKIM (exact key strength) / DMARC / BIMI / MTA-STS / TLS-RPT,
        web security headers and <code>security.txt</code>, the TLS certificate from
        Certificate Transparency logs, RDAP via the IANA bootstrap registry with a
        domain-age signal, reverse DNS + Team Cymru IP-to-ASN for hosting and network
        intelligence, subdomain discovery with dangling-CNAME takeover detection,
        ~2,800 tech fingerprints (headers, cookies, HTML, meta tags, JS) with
        dependency inference, the email-platform and vendor surface from MX / TXT /
        homepage links / subdomains / DMARC reporting endpoints, the Microsoft 365
        tenant cloud (Commercial / GCC High / DoD / 21Vianet) resolved authoritatively
        from Microsoft&apos;s federation endpoint, OSV.dev CVE matching for detected
        versions, and HTTP-probe exposure checks (.git, .env, admin panels, and the
        like).
      </p>

      <h2>The report</h2>
      <p>
        The scan streams live: each analyzer reports the moment it settles, so you
        watch real progress instead of a spinner. The finished report covers every
        analyzer with an overall security score (0 to 10), per-module scores, an
        executive narrative with prioritized fixes, ready-to-paste remediation for each
        fixable finding (DNS records for SPF / DMARC / DKIM / MTA-STS / TLS-RPT / CAA, a{' '}
        <code>security.txt</code> template, and security-header config for nginx /
        Caddy / Cloudflare), and a mapping of findings to common compliance frameworks
        (CMMC Level 2, NIST 800-171, SOC 2, ISO 27001, NIST CSF, PCI DSS). The whole
        report can be exported to a styled PDF, generated entirely in your browser.
      </p>
      <p>
        The full result is returned to your browser and is{' '}
        <strong>never stored on our servers</strong>. It is kept in your browser for
        the session (so a refresh or the back-button keeps the report); it is cleared
        when you close the tab.
      </p>
      <p>
        Compliance mapping is a heuristic indication from external signals only. It
        is not an audit, attestation, or certification. See the{' '}
        <a href="/terms">Terms of Service</a>.
      </p>

      <h2>Rate limiting and verification</h2>
      <p>
        Scans are protected by a per-IP rate limit (a sliding window of a few scans
        per minute, Cloudflare-edge-attributed via <code>cf-connecting-ip</code>)
        and by a Cloudflare Turnstile human-verification challenge. These controls
        keep the free tool available and prevent abuse; do not attempt to evade or
        automate around them.
      </p>

      <h2>Privacy</h2>
      <p>
        Scan results are never written to a database. We keep only an aggregate,
        IP-free operator log for service quality and abuse monitoring. For the full
        details, see the <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>License</h2>
      <p>
        Source code on{' '}
        <a href="https://github.com/masoud-heydari/protbyte-sentinel" target="_blank" rel="noreferrer">GitHub</a>,{' '}
        <a href="https://github.com/masoud-heydari/protbyte-sentinel/blob/main/LICENSE" target="_blank" rel="noreferrer">MIT License</a>.
        Data sources (Cloudflare DoH, IANA RDAP, certspotter, crt.sh, Team Cymru,
        OSV.dev) each have their own usage terms.
      </p>

      <h2>Contact</h2>
      <p>
        Bug reports, integration help, or "please add &lt;X&gt;" — email{' '}
        <a href="mailto:contact@protbyte.com">contact@protbyte.com</a> or open
        an issue on{' '}
        <a href="https://github.com/masoud-heydari/protbyte-sentinel/issues" target="_blank" rel="noreferrer">GitHub</a>.
      </p>
    </LegalLayout>
  );
}
